import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { StyleSheet, View, useWindowDimensions } from "react-native";
import { useTheme } from "react-native-elements";
import { useLayoutType } from "./Miscellaneous";
import {
  TooltipContent,
  TooltipContext,
  TooltipControlContext,
} from "./TooltipContext";
import { VisibilityContext } from "./VisibilityContext";

export const TooltipDisplay = () => {
  const tooltipContext = useContext(TooltipContext);
  const theme = useTheme();
  const windowDimensions = useWindowDimensions();
  const layout = useLayoutType();
  return useMemo(() => {
    return (
      <View
        style={{
          zIndex: 800,
          display:
            tooltipContext.tooltip != null && layout.showSpellMenu
              ? undefined
              : "none",
          position: "absolute",
          left: tooltipContext.position?.left,
          top: tooltipContext.position?.top,
          bottom: tooltipContext.position?.bottom,
          right: tooltipContext.position?.right,
          minWidth: tooltipContext.position.containerWidth,
          alignItems: "center",
        }}
        pointerEvents="none"
      >
        <View
          style={{
            padding: 8,
            alignSelf: "center",
            backgroundColor: theme.theme.colors.background,
            borderRadius: 4,
            borderWidth: 1,
            borderColor: theme.theme.colors.grey3,
            maxWidth: Math.min(
              windowDimensions.width - (tooltipContext.position?.left ?? 0),
              tooltipContext.position?.right ?? windowDimensions.width,
              windowDimensions.width / 3,
              480,
            ),
          }}
          pointerEvents="none"
        >
          {tooltipContext.tooltip}
        </View>
      </View>
    );
  }, [
    tooltipContext.tooltip,
    JSON.stringify(tooltipContext.position),
    theme,
    windowDimensions.width,
    layout.showSpellMenu,
  ]);
};

export enum TooltipPositionType {
  DEFAULT = "DEFAULT",
  LEFT_OF_ELEMENT = "LEFT_OF_ELEMENT",
  RIGHT_OF_ELEMENT = "RIGHT_OF_ELEMENT",
}

export const TooltipWrapper = (props: {
  children: any;
  tooltip: TooltipContent;
  tooltipId: string;
  tooltipPosition?: TooltipPositionType;
  containerStyle?: any;
}) => {
  const tooltipPositionType =
    props.tooltipPosition ?? TooltipPositionType.DEFAULT;
  const tooltipContext = useContext(TooltipControlContext);
  const visible = useContext(VisibilityContext);

  const screenDimensions = useWindowDimensions();

  const wrapperRef = useRef<View | null>(null);

  const measureAndSetTooltip = useCallback(
    (measurementId?: number) => {
      wrapperRef.current?.measure((x, y, width, height, pageX, pageY) => {
        switch (tooltipPositionType) {
          case TooltipPositionType.DEFAULT:
            if (pageY > screenDimensions.height / 2) {
              tooltipContext.setTooltip(
                props.tooltipId,
                props.tooltip,
                {
                  left: pageX,
                  containerWidth: width,
                  bottom: screenDimensions.height - pageY + 4,
                },
                measurementId,
              );
            } else {
              tooltipContext.setTooltip(
                props.tooltipId,
                props.tooltip,
                {
                  top: pageY + height + 4,
                  left: pageX,
                  containerWidth: width,
                },
                measurementId,
              );
            }
            break;
          case TooltipPositionType.RIGHT_OF_ELEMENT:
            if (pageY > screenDimensions.height / 2) {
              tooltipContext.setTooltip(
                props.tooltipId,
                props.tooltip,
                {
                  left: Math.min(
                    pageX + width + 4,
                    screenDimensions.width - 480,
                  ),
                  containerWidth: 30,
                  bottom: Math.max(0, screenDimensions.height - pageY - height),
                },
                measurementId,
              );
            } else {
              tooltipContext.setTooltip(
                props.tooltipId,
                props.tooltip,
                {
                  top: pageY,
                  left: Math.min(
                    pageX + width + 4,
                    screenDimensions.width - 480,
                  ),
                  containerWidth: 30,
                },
                measurementId,
              );
            }
            break;
          case TooltipPositionType.LEFT_OF_ELEMENT:
            if (pageY > screenDimensions.height / 2) {
              tooltipContext.setTooltip(
                props.tooltipId,
                props.tooltip,
                {
                  right: screenDimensions.width - pageX + 4,
                  containerWidth: 30,
                  bottom: Math.max(0, screenDimensions.height - pageY - height),
                },
                measurementId,
              );
            } else {
              tooltipContext.setTooltip(
                props.tooltipId,
                props.tooltip,
                {
                  top: pageY,
                  right: screenDimensions.width - pageX + 4,
                  containerWidth: 30,
                },
                measurementId,
              );
            }
            break;
        }
      });
    },
    [
      tooltipContext.setTooltip,
      props.tooltip,
      props.tooltipId,
      wrapperRef,
      screenDimensions.height,
      tooltipPositionType,
    ],
  );

  const onMouseOver = useCallback(() => {
    const measurementId = Math.random();
    tooltipContext.onMouseEnter(props.tooltipId, measurementId);
    measureAndSetTooltip(measurementId);
  }, [props.tooltipId, measureAndSetTooltip]);
  const onMouseOut = useCallback(() => {
    tooltipContext.onMouseLeave(props.tooltipId);
  }, [props.tooltipId]);

  useEffect(() => {
    measureAndSetTooltip();
  }, [measureAndSetTooltip]);

  useEffect(() => {
    if (!visible) {
      tooltipContext.onUnmountTooltip(props.tooltipId);
    }
  }, [visible, tooltipContext.clearTooltip, props.tooltipId]);

  useEffect(() => {
    if (props.tooltip == null) {
      tooltipContext.clearTooltip(props.tooltipId);
    }
  }, [props.tooltip, props.tooltipId, tooltipContext.clearTooltip]);

  useEffect(() => {
    measureAndSetTooltip();
    return () => tooltipContext.onUnmountTooltip(props.tooltipId);
  }, [props.tooltipId]);

  return useMemo(
    () => (
      <View
        ref={wrapperRef}
        style={[styles.tooltipWrapper, props.containerStyle]}
        // @ts-expect-error React Native Web supports this
        onMouseEnter={onMouseOver}
        onMouseLeave={onMouseOut}
      >
        {props.children}
      </View>
    ),
    [wrapperRef, props.children, props.containerStyle, onMouseOver, onMouseOut],
  );
};

const styles = StyleSheet.create({
  tooltipWrapper: {
    alignSelf: "stretch",
    flex: 1,
  },
});
